import { Component, EventEmitter, HostListener, OnInit, Output, ViewChild, Input, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { api, PagedSearchResults } from '@bayclubs/utils';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { camps } from '../../models/models.generated';
import { SelectedMember } from '../../models/filters/selected-member.model';
import { MembersApiService } from '../../services/api/members-api.service';

@Component({
    selector: 'app-member-search',
    templateUrl: './member-search.component.html',
    styleUrls: ['./member-search.component.scss'],
    standalone: false
})
export class MemberSearchComponent {
    public results: camps.MemberInfoModel[] = [];

    public searchForm: UntypedFormGroup;
    public pageSize: number = 10;

    public searchResults: PagedSearchResults = new PagedSearchResults();

    public resultsVisible: boolean = false;

    public isBusy: boolean = false;

    private nameSetManually: boolean = false;

    @Input() public currentMember: SelectedMember;

    @Output() public membershipSelected: EventEmitter<SelectedMember> = new EventEmitter<SelectedMember>();

    @ViewChild('resultsElement', { static: true }) resultsElement;

    constructor(private api: MembersApiService, private formBuilder: UntypedFormBuilder) { }

    ngOnInit() {
        this.searchForm = this.formBuilder.group({
            expression: ['', []]
        });

        this.searchForm
            .get('expression')
            .valueChanges.pipe(debounceTime(500))
            .subscribe(value => {
                if (this.nameSetManually) {
                    this.nameSetManually = false;
                    return;
                }

                if (value && value.length > 2) {
                    this.onSearch(0);
                } else {
                    this.resultsVisible = false;
                }

                if (!value || value.length == 0) {
                    this.membershipSelected.emit(null);
                }
            });

        if (this.currentMember) {
            this.setSelectedMember(this.currentMember);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['currentMember'] && this.searchForm && this.currentMember) {
            this.setSelectedMember(this.currentMember);
        }
    }

    public get hasItems() {
        return this.results && this.results.length > 0;
    }

    public onItemClick(item: camps.MemberInfoModel) {
        this.resultsVisible = false;

        this.setSelectedMember(<SelectedMember>{
            mainMember: item.fullName,
            mainMemberId: item.memberId,
            membershipId: '',
            children: item.children
        });
    }

    private setSelectedMember(member: SelectedMember) {
        this.nameSetManually = true;
        this.searchForm.controls['expression'].setValue(member.mainMember + ' ' + member.mainMemberId);
        this.membershipSelected.emit(member);
    }

    @HostListener('document:click', ['$event'])
    clickout(event) {
        if (this.resultsElement.nativeElement.contains(event.target)) {
            this.resultsVisible = true;
        } else {
            this.resultsVisible = false;
        }
    }

    onSearch(numberOfPagesToSkip: number = 0) {
        this.isBusy = true;

        this.getMemberApiPromise(numberOfPagesToSkip).subscribe(result => {
            this.resultsVisible = true;
            if (numberOfPagesToSkip === 0) {
                this.results = [];
            }

            if (result.isSuccess) {
                this.searchResults.pageNumber = result.pageNumber;
                this.searchResults.totalCount = result.totalCount;
                this.searchResults.totalPages = result.totalPages;
                this.searchResults.pages = Array<number>();

                for (var j = 1; j <= this.searchResults.totalPages; j++) {
                    this.searchResults.pages.push(j);
                }

                for (var i = 0; i < result.pageItems.length; i++) {
                    this.results.push(result.pageItems[i]);
                }
            }

            this.isBusy = false;
        });
    }

    public onScroll() {
        if (this.searchResults.canNextPage) {
            this.onSearch(this.searchResults.nextPageNumber);
        }
    }

    private getMemberApiPromise(numberOfPagesToSkip: number): Observable<api.OperationPagedResult<camps.MemberInfoModel>> {
        var request = this.prepareRequest(numberOfPagesToSkip);
        return this.api.search(request);
    }

    private prepareRequest(numberOfPagesToSkip: number = 0): camps.MemberSearchRequest {
        var request = <camps.MemberSearchRequest>{
            expression: this.searchForm.value.expression,
            take: this.pageSize,
            skip: numberOfPagesToSkip * this.pageSize
        };

        return request;
    }

    public onEnter() {
        if (this.isBusy) {
            return;
        }

        this.onSearch();
    }

    public showClear() {
        return this.searchForm.value.expression && this.searchForm.value.expression.length > 0;
    }

    public onClear() {
        this.membershipSelected.emit(null);
        this.searchForm.controls['expression'].patchValue(null);
    }
}
